/**
 * Trend Analysis Service
 *
 * Computes a year-end projection for PROA metrics based on historical
 * seasonality from the last N years (default 2).
 *
 * Method:
 *  1. For each prior year, compute a monthly "% of annual total" curve
 *     from the realized monthly values (captures natural seasonality —
 *     e.g., Promo year-end spikes).
 *  2. Average the curves across years (simple mean).
 *  3. Sum the average curve up to the current point (prorated by day
 *     within the current month) → expectedPaceRatio (share of the year
 *     already done, per history).
 *  4. Projected year-end = realYTD + PROA × (1 − expectedPaceRatio).
 *     Hybrid method: what's realized is a fact; the remaining months are
 *     assumed to hit their proportional PROA share (respecting seasonality).
 *     This avoids the old pitfall where an early bump was extrapolated across
 *     the whole year (including the Promo peak in December).
 *  5. Compare projection against PROA → status (above / on / at-risk / off track).
 *
 * Thresholds:
 *   projected ≥ 100% of PROA → on-track
 *   projected 90% – 99%      → at-risk
 *   projected < 90%          → off-track
 */

import { trpc } from '@/api/trpc'
import { useMemo } from 'react'
import { toNum } from './financial-analytics.service'
import {
  averageCurveAcrossYears,
  buildMonthlyAggregates,
  normalizeToPercentCurves,
} from './seasonality-utils'

const YEARS_BACK = 2
const ABOVE_TRACK_THRESHOLD = 1.05 // ≥ 105% → above-track
const ON_TRACK_THRESHOLD = 0.97 // 97% – 104% → on-track
const AT_RISK_THRESHOLD = 0.9 // 90% – 96% → at-risk

export type TrendMetricKey = 'receita' | 'lucroLiquido' | 'faturamento'

export type DREComponentKey = 'receita' | 'folha' | 'terceiros' | 'despesas' | 'impostos' | 'receitaFinanceira'

export interface ComponentProjection {
  projectedValue: number
  realYTD: number
  proaAnnual: number
  projectedPercent: number
}

export interface TrendResult {
  status: 'above-track' | 'on-track' | 'at-risk' | 'off-track'
  /** Projected year-end value in R$ */
  projectedValue: number
  /** Projected value as a ratio of PROA (1.0 = 100%) */
  projectedPercent: number
  /** Expected % of annual total achieved by the current point (0–1) */
  expectedPaceRatio: number
  /** Realized value YTD for the current year (R$) */
  realYTD: number
  /** PROA annual target (R$) */
  proaAnnual: number
  /** Human label for the current point (e.g., "Abril") */
  currentMonthLabel: string
  /** Month index used for expected pace (1-12) */
  currentMonth: number
  /** Current calendar year (the year being projected) */
  currentYear: number
  /** Fraction of current month elapsed (0–1) */
  monthProgress: number
  /** Historical years used in the curve */
  historicalYears: number[]
  /** Per-year normalized curve: year → [12 monthly ratios summing to ~1] */
  historicalCurves: Record<number, number[]>
  /** Mean curve across historical years (12 entries, sum ~1) */
  averageCurve: number[]

  // ── Bottom-up projection fields (Lucro only) ──────────────────────
  /** When true, the projection was derived from bottom-up DRE component
   *  decomposition rather than direct seasonality. Used only for Lucro. */
  bottomUp?: boolean
  /** Per-component projected values (only present when bottomUp is true) */
  componentProjections?: Record<DREComponentKey, ComponentProjection>

  // ── Extrapolation mode (metrics without PROA) ─────────────────────
  /** When set, projectedPercent compares vs this year's total (not PROA) */
  vsYear?: number
}

const MONTH_NAMES_PT = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
]

export interface MonthRow {
  Ano?: number | string | null
  Mes?: number | string | null
  ReceitaBruta?: unknown
  LucroLiquido?: unknown
  FaturamentoTotal?: unknown
  Folha?: unknown
  ServicosPrestados?: unknown
  DespesasOperacionais?: unknown
  PIS?: unknown
  COFINS?: unknown
  ISS?: unknown
  ReceitasFinanceiras?: unknown
}

function getMetricValue(row: MonthRow, metric: TrendMetricKey): number {
  switch (metric) {
    case 'receita': return toNum(row.ReceitaBruta)
    case 'lucroLiquido': return toNum(row.LucroLiquido)
    case 'faturamento': return toNum(row.FaturamentoTotal)
  }
}

function getComponentValue(row: MonthRow, key: DREComponentKey): number {
  switch (key) {
    case 'receita': return toNum(row.ReceitaBruta)
    case 'folha': return Math.abs(toNum(row.Folha))
    case 'terceiros': return Math.abs(toNum(row.ServicosPrestados))
    case 'despesas': return Math.abs(toNum(row.DespesasOperacionais))
    case 'impostos': return Math.abs(toNum(row.PIS)) + Math.abs(toNum(row.COFINS)) + Math.abs(toNum(row.ISS))
    case 'receitaFinanceira': return toNum(row.ReceitasFinanceiras)
  }
}

/**
 * Given an ISO end date (YYYY-MM-DD), compute the 0-based month index and
 * the fraction of that month that has elapsed.
 */
function getMonthProgress(endIso: string): { monthIdx: number; progress: number } {
  const d = new Date(endIso + 'T00:00:00')
  if (Number.isNaN(d.getTime())) return { monthIdx: 0, progress: 0 }
  const year = d.getFullYear()
  const month = d.getMonth() // 0-11
  const dayOfMonth = d.getDate()
  const daysInMonth = new Date(year, month + 1, 0).getDate()
  return { monthIdx: month, progress: dayOfMonth / daysInMonth }
}

interface UseTrendAnalysisParams {
  /** Period end date (YYYY-MM-DD). Used as the "current point in the year". */
  endDate: string
  /** PROA annual targets per metric (R$). Pass 0 or undefined to skip. */
  proaReceita: number
  proaLucroLiquido: number
  proaFaturamento?: number
  /** Component PROAs for bottom-up Lucro projection (all positive R$) */
  proaFolha?: number
  proaTerceiros?: number
  proaDespesas?: number
  proaImpostos?: number
  proaReceitaFinanceira?: number
  /**
   * Optional R$ amount to ADD to the Folha component realYTD in the
   * bottom-up projection. Covers months where folha was incurred but
   * hasn't hit the DRE yet (cash-basis lag). Without this, the
   * projected folha (and therefore Lucro) would be artificially
   * optimistic. Pass the total from useFolhaGapEstimate.
   */
  folhaMissingEstimate?: number
  /**
   * Optional R$ amount to ADD to the Terceiros component realYTD in
   * the bottom-up projection. Covers committed third-party costs that
   * haven't hit the DRE yet. Same rationale as folhaMissingEstimate.
   */
  terceirosMissingEstimate?: number
  /**
   * Optional R$ amount to subtract from the Lucro Líquido realYTD for
   * display purposes. The bottom-up projection does NOT use this value
   * in the formula (each component has its own curve), but the realYTD
   * shown to the user reflects the Ajustado for consistency.
   */
  lucroLiquidoYTDDeduction?: number
  enabled?: boolean
}

export interface TrendAnalysisResult {
  receita: TrendResult | null
  lucroLiquido: TrendResult | null
  faturamento: TrendResult | null
  /** Per-component trend results (available when bottom-up projection succeeds) */
  componentTrends: Partial<Record<DREComponentKey, TrendResult>>
  isLoading: boolean
}

/**
 * Hook: fetches 2 years of historical DRE data + current YTD and computes a
 * trend projection for the headline PROA metrics.
 */
export function useTrendAnalysis({
  endDate,
  proaReceita,
  proaLucroLiquido,
  proaFaturamento = 0,
  proaFolha = 0,
  proaTerceiros = 0,
  proaDespesas = 0,
  proaImpostos = 0,
  proaReceitaFinanceira = 0,
  folhaMissingEstimate = 0,
  terceirosMissingEstimate = 0,
  lucroLiquidoYTDDeduction = 0,
  enabled = true,
}: UseTrendAnalysisParams): TrendAnalysisResult {
  const currentYear = parseInt(endDate.substring(0, 4), 10) || new Date().getFullYear()
  const historicalStart = `${currentYear - YEARS_BACK}-01-01`

  const { data: rows = [], isLoading } = trpc.dre.getReport.useQuery(
    {
      dataInicial: new Date(historicalStart),
      dataFinal: new Date(endDate),
    },
    {
      enabled: enabled && !!endDate,
      retry: false,
      staleTime: 5 * 60 * 1000,
    }
  )

  return useMemo(() => {
    const empty: TrendAnalysisResult = {
      receita: null,
      lucroLiquido: null,
      faturamento: null,
      componentTrends: {},
      isLoading,
    }
    if (!rows.length) return empty

    const { monthIdx, progress } = getMonthProgress(endDate)
    const currentMonth = monthIdx + 1
    const currentMonthLabel = MONTH_NAMES_PT[monthIdx] ?? ''

    const results: Record<TrendMetricKey, TrendResult | null> = {
      receita: null,
      lucroLiquido: null,
      faturamento: null,
    }

    // Helper: build a seasonality-based TrendResult for a given value
    // extractor and PROA target.
    function buildSeasonalityResult(
      extractValue: (row: MonthRow) => number,
      proaAnnual: number,
    ): TrendResult | null {
      const byYear = buildMonthlyAggregates(
        rows as MonthRow[],
        (r) => Number(r.Ano),
        (r) => Number(r.Mes),
        extractValue,
      )

      const historicalYears = Array.from(byYear.keys())
        .filter((y) => y < currentYear)
        .sort((a, b) => a - b)

      if (historicalYears.length === 0) return null

      const allCurves = normalizeToPercentCurves(byYear)
      const historicalCurves: Record<number, number[]> = {}
      for (const year of historicalYears) {
        historicalCurves[year] = allCurves[year]
      }

      const averageCurve = averageCurveAcrossYears(historicalCurves, historicalYears)
      const expectedPaceRatio =
        averageCurve.slice(0, monthIdx).reduce((s, v) => s + v, 0) +
        (averageCurve[monthIdx] ?? 0) * progress

      const currentYearMonths = byYear.get(currentYear) ?? new Array(12).fill(0)
      const realYTD = currentYearMonths.reduce((s, v) => s + v, 0)

      if (proaAnnual <= 0 || expectedPaceRatio <= 0 || realYTD === 0) return null

      const remainingPaceRatio = Math.max(0, 1 - expectedPaceRatio)
      const projectedValue = realYTD + proaAnnual * remainingPaceRatio
      const projectedPercent = projectedValue / proaAnnual

      const status: TrendResult['status'] =
        projectedPercent >= ABOVE_TRACK_THRESHOLD
          ? 'above-track'
          : projectedPercent >= ON_TRACK_THRESHOLD
            ? 'on-track'
            : projectedPercent >= AT_RISK_THRESHOLD
              ? 'at-risk'
              : 'off-track'

      return {
        status,
        projectedValue,
        projectedPercent,
        expectedPaceRatio,
        realYTD,
        proaAnnual,
        currentMonthLabel,
        currentMonth,
        currentYear,
        monthProgress: progress,
        historicalYears,
        historicalCurves,
        averageCurve,
      }
    }

    // Helper: pure extrapolation for metrics without PROA.
    // Projects via `realYTD / expectedPaceRatio` and compares to the
    // previous year's total (growth indicator).
    function buildExtrapolationResult(
      extractValue: (row: MonthRow) => number,
    ): TrendResult | null {
      const byYear = buildMonthlyAggregates(
        rows as MonthRow[],
        (r) => Number(r.Ano),
        (r) => Number(r.Mes),
        extractValue,
      )

      const historicalYears = Array.from(byYear.keys())
        .filter((y) => y < currentYear)
        .sort((a, b) => a - b)

      if (historicalYears.length === 0) return null

      const allCurves = normalizeToPercentCurves(byYear)
      const historicalCurves: Record<number, number[]> = {}
      for (const year of historicalYears) {
        historicalCurves[year] = allCurves[year]
      }

      const averageCurve = averageCurveAcrossYears(historicalCurves, historicalYears)
      const expectedPaceRatio =
        averageCurve.slice(0, monthIdx).reduce((s, v) => s + v, 0) +
        (averageCurve[monthIdx] ?? 0) * progress

      const currentYearMonths = byYear.get(currentYear) ?? new Array(12).fill(0)
      const realYTD = currentYearMonths.reduce((s, v) => s + v, 0)

      if (expectedPaceRatio <= 0 || realYTD === 0) return null

      const projectedValue = realYTD / expectedPaceRatio

      // Compare vs previous year total
      const prevYear = historicalYears[historicalYears.length - 1]
      const prevYearMonths = byYear.get(prevYear) ?? new Array(12).fill(0)
      const prevYearTotal = prevYearMonths.reduce((s, v) => s + v, 0)

      if (prevYearTotal <= 0) return null

      const projectedPercent = projectedValue / prevYearTotal

      const status: TrendResult['status'] =
        projectedPercent >= ABOVE_TRACK_THRESHOLD
          ? 'above-track'
          : projectedPercent >= ON_TRACK_THRESHOLD
            ? 'on-track'
            : projectedPercent >= AT_RISK_THRESHOLD
              ? 'at-risk'
              : 'off-track'

      return {
        status,
        projectedValue,
        projectedPercent,
        expectedPaceRatio,
        realYTD,
        proaAnnual: prevYearTotal,
        currentMonthLabel,
        currentMonth,
        currentYear,
        monthProgress: progress,
        historicalYears,
        historicalCurves,
        averageCurve,
        vsYear: prevYear,
      }
    }

    // 1. Compute Receita & Faturamento via seasonality (stable curves)
    results.receita = buildSeasonalityResult(
      (r) => getMetricValue(r, 'receita'),
      proaReceita,
    )
    // Faturamento: use PROA if available, otherwise extrapolate vs previous year
    results.faturamento = proaFaturamento > 0
      ? buildSeasonalityResult(
          (r) => getMetricValue(r, 'faturamento'),
          proaFaturamento,
        )
      : buildExtrapolationResult(
          (r) => getMetricValue(r, 'faturamento'),
        )

    // 2. Compute Lucro via bottom-up DRE component decomposition
    //
    // The seasonality curve for Lucro is too volatile (small-denominator
    // amplification). Instead, project each DRE component individually
    // via its own stable seasonality curve, then reconstruct Lucro as:
    //   Lucro = Receita − Folha − Terceiros − Despesas − Impostos + RecFin
    //
    // Falls back to direct Lucro seasonality if any component is missing.
    // Will be populated inside the bottom-up block with full TrendResult
    // objects (including historical curves) for individual card badges.
    let componentTrends: Partial<Record<DREComponentKey, TrendResult>> = {}

    const componentPROAs: Record<DREComponentKey, number> = {
      receita: proaReceita,
      folha: proaFolha,
      terceiros: proaTerceiros,
      despesas: proaDespesas,
      impostos: proaImpostos,
      receitaFinanceira: proaReceitaFinanceira,
    }

    const allComponentKeys: DREComponentKey[] = [
      'receita', 'folha', 'terceiros', 'despesas', 'impostos', 'receitaFinanceira',
    ]
    const hasAllComponentPROAs = allComponentKeys.every((k) => componentPROAs[k] > 0)

    if (hasAllComponentPROAs && proaLucroLiquido > 0) {
      const componentResults: Partial<Record<DREComponentKey, TrendResult>> = {}
      let allComponentsOk = true

      for (const key of allComponentKeys) {
        const result = buildSeasonalityResult(
          (r) => getComponentValue(r as MonthRow, key),
          componentPROAs[key],
        )
        if (!result) {
          allComponentsOk = false
          break
        }
        componentResults[key] = result
      }

      if (allComponentsOk) {
        const cp = componentResults as Record<DREComponentKey, TrendResult>

        // Adjust components for cash-basis lag. The DRE understates
        // costs that have been incurred but not yet paid. Without this,
        // the bottom-up projection treats the gap as "savings" and
        // inflates the Lucro projection.
        function adjustComponent(key: DREComponentKey, missing: number) {
          if (missing <= 0) return
          const c = cp[key]
          const adjustedRealYTD = c.realYTD + missing
          const adjustedProjected = adjustedRealYTD + c.proaAnnual * Math.max(0, 1 - c.expectedPaceRatio)
          cp[key] = {
            ...c,
            realYTD: adjustedRealYTD,
            projectedValue: adjustedProjected,
            projectedPercent: adjustedProjected / c.proaAnnual,
          }
        }
        adjustComponent('folha', folhaMissingEstimate)
        adjustComponent('terceiros', terceirosMissingEstimate)

        // Expose full TrendResult objects (with curves + adjustments) for card badges
        componentTrends = { ...cp }

        const projectedValue =
          cp.receita.projectedValue
          - cp.folha.projectedValue
          - cp.terceiros.projectedValue
          - cp.despesas.projectedValue
          - cp.impostos.projectedValue
          + cp.receitaFinanceira.projectedValue

        const projectedPercent = projectedValue / proaLucroLiquido

        // Lucro YTD for display (adjusted for consistency with the card)
        const byYearLucro = buildMonthlyAggregates(
          rows as MonthRow[],
          (r) => Number(r.Ano),
          (r) => Number(r.Mes),
          (r) => getMetricValue(r, 'lucroLiquido'),
        )
        const currentYearLucro = byYearLucro.get(currentYear) ?? new Array(12).fill(0)
        let realYTD = currentYearLucro.reduce((s, v) => s + v, 0)
        if (lucroLiquidoYTDDeduction > 0) {
          realYTD -= lucroLiquidoYTDDeduction
        }

        const status: TrendResult['status'] =
          projectedPercent >= ABOVE_TRACK_THRESHOLD
            ? 'above-track'
            : projectedPercent >= ON_TRACK_THRESHOLD
              ? 'on-track'
              : projectedPercent >= AT_RISK_THRESHOLD
                ? 'at-risk'
                : 'off-track'

        const componentProjections: Record<DREComponentKey, ComponentProjection> = {} as any
        for (const key of allComponentKeys) {
          componentProjections[key] = {
            projectedValue: cp[key].projectedValue,
            realYTD: cp[key].realYTD,
            proaAnnual: cp[key].proaAnnual,
            projectedPercent: cp[key].projectedPercent,
          }
        }

        results.lucroLiquido = {
          status,
          projectedValue,
          projectedPercent,
          expectedPaceRatio: cp.receita.expectedPaceRatio,
          realYTD,
          proaAnnual: proaLucroLiquido,
          currentMonthLabel,
          currentMonth,
          currentYear,
          monthProgress: progress,
          historicalYears: cp.receita.historicalYears,
          historicalCurves: cp.receita.historicalCurves,
          averageCurve: cp.receita.averageCurve,
          bottomUp: true,
          componentProjections,
        }
      } else {
        // Fallback: direct Lucro seasonality
        results.lucroLiquido = buildSeasonalityResult(
          (r) => getMetricValue(r, 'lucroLiquido'),
          proaLucroLiquido,
        )
      }
    } else {
      // Fallback: direct Lucro seasonality
      results.lucroLiquido = buildSeasonalityResult(
        (r) => getMetricValue(r, 'lucroLiquido'),
        proaLucroLiquido,
      )
    }

    return { ...results, componentTrends, isLoading }
  }, [rows, isLoading, endDate, currentYear, proaReceita, proaLucroLiquido, proaFaturamento, proaFolha, proaTerceiros, proaDespesas, proaImpostos, proaReceitaFinanceira, folhaMissingEstimate, terceirosMissingEstimate, lucroLiquidoYTDDeduction])
}

export const TREND_STATUS_CONFIG: Record<
  TrendResult['status'],
  { label: string; color: string; bg: string; dot: string; border: string }
> = {
  'above-track': {
    label: 'Acima do pace',
    color: 'text-brand-primary',
    bg: 'bg-brand-primary/10',
    dot: 'bg-brand-primary',
    border: 'border border-transparent',
  },
  'on-track': {
    label: 'No pace',
    color: 'text-brand-primary',
    bg: 'bg-transparent',
    dot: 'bg-brand-primary',
    border: 'border border-brand-primary',
  },
  'at-risk': {
    label: 'Em risco',
    color: 'text-yellow-400',
    bg: 'bg-yellow-400/10',
    dot: 'bg-yellow-400',
    border: 'border border-transparent',
  },
  'off-track': {
    label: 'Abaixo do pace',
    color: 'text-red-400',
    bg: 'bg-red-400/10',
    dot: 'bg-red-400',
    border: 'border border-transparent',
  },
}
